import { getItem, setItem } from './sessionStorageUtils';

/**
 * Session storage key for storing SRP data object.
 * @constant {string}
 */
export const SRP_PATH_KEY = 'sessionSrpPathData';

/**
 * Regular expression patterns for identifying Search Results Page URLs.
 * Used to determine if a given URL is an SRP that should be tracked.
 * @constant {RegExp[]}
 */
export const srpPatterns = [
    /^\/cars-for-sale(?:\/|$)/, // Main SRP routes
    /^\/all(?:\/|$)/, // Alternative SRP routes
    /^\/all-cars(?:\/|$)/, // Alternative SRP routes
    /^\/new-cars(?:\/|$)/, // New cars SRP
    /^\/used-cars(?:\/|$)/, // Used cars SRP
    /^\/certified(?:\/|$)/, // Certified cars SRP
];

/**
 * Gets the current scroll position of the window.
 * Provides cross-browser compatibility for retrieving scroll coordinates.
 *
 * @param {string} [path] - Path to use for scroll position key
 * @returns {Object} Object containing scroll coordinates
 * @returns {number} returns.left - Horizontal scroll position
 * @returns {number} returns.top - Vertical scroll position
 */
export function getScrollPosition(path) {
    const key = `scrollPos:${path?.split('?')?.at(0)}`;
    const { x: left = 0, y: top = 0 } = getItem(key, true) || {};

    return {
        left,
        top,
    };
}

/**
 * Retrieves the complete SRP data object from session storage.
 *
 * @param {Object} [options] - Configuration options
 * @param {string} [options.srpPathDataKey=SRP_PATH_KEY] - Session storage key to use
 * @returns {Object} The stored SRP data object, or empty object if not found
 * @returns {string} returns.path - The stored SRP path
 * @returns {Object} returns.scrollPosition - The stored scroll position
 * @returns {number} returns.scrollPosition.left - Horizontal scroll position
 * @returns {number} returns.scrollPosition.top - Vertical scroll position
 */
export function getSrpDataFromSessionStorage({ srpPathDataKey = SRP_PATH_KEY } = {}) {
    return getItem(srpPathDataKey, true) ?? {};
}

/**
 * Retrieves just the SRP path from session storage with fallback.
 * This is a convenience function for when you only need the path and not the scroll position.
 *
 * @param {Object} [options] - Configuration options
 * @param {string} [options.srpPathDataKey=SRP_PATH_KEY] - Session storage key to use
 * @returns {string|undefined} The stored SRP path if it is available, or undefined if not found
 */
export function getSrpPathFromSessionStorage({ srpPathDataKey = SRP_PATH_KEY } = {}) {
    return getSrpDataFromSessionStorage({ srpPathDataKey })?.path;
}

/**
 * Stores SRP path and current scroll position in session storage.
 * Automatically captures the current scroll position when called, ensuring
 * the user's exact position is preserved for restoration.
 *
 * @param {Object} [options] - Configuration options
 * @param {string} [options.srpPathDataKey=SRP_PATH_KEY] - Session storage key to use
 * @param {string} options.path - The SRP path to store
 */
export function setSrpPathInSessionStorage({
    srpPathDataKey = SRP_PATH_KEY,
    path,
} = {}) {
    const scrollPosition = getScrollPosition(path);
    const data = { path, scrollPosition };
    setItem(srpPathDataKey, data, true);
}

/**
 * Retrieves just the scroll position from stored SRP data.
 * This is a convenience function for scroll restoration functionality.
 *
 * @returns {Object} Object containing scroll coordinates with fallback values
 * @returns {number} returns.left - Horizontal scroll position (0 if not found)
 * @returns {number} returns.top - Vertical scroll position (0 if not found)
 */
export function getScrollPositionForSrp() {
    const { scrollPosition = {} } = getSrpDataFromSessionStorage();
    return { left: 0, top: 0, ...scrollPosition };
}
